import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Images, Package, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import type { UpdateProductPayload } from '@/lib/api/product-api'
import type { ProductsSearch } from './index'

import { FormSection } from '@/components/forms/form-section'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ComboRecipeSection } from '@/components/products/combo-recipe-section'
import { ProductForm } from '@/components/products/product-form'
import { ProductImageGallery } from '@/components/products/product-image-gallery'
import { ProductStockPanel } from '@/components/products/product-stock-panel'
import {
    useDeleteProduct,
    useDeleteProductImage,
    useProduct,
    useProductStockBreakdown,
    useSetPrimaryImage,
    useUpdateProduct,
    useUploadProductImage,
} from '@/hooks/use-product-query'
import { LIST_SEARCH_KEYS, recallListSearch } from '@/lib/list-search-store'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/products/$productId',
)({
    component: ProductDetailPage,
})

function ProductDetailPage() {
    const { productId } = Route.useParams()
    const navigate = useNavigate()

    const { data: product, isLoading } = useProduct(productId)
    const { data: stock, isLoading: stockLoading } =
        useProductStockBreakdown(productId)
    const updateProduct = useUpdateProduct()
    const deleteProduct = useDeleteProduct()
    const uploadImage = useUploadProductImage()
    const deleteImage = useDeleteProductImage()
    const setPrimary = useSetPrimaryImage()

    const backSearch = () =>
        recallListSearch<ProductsSearch>(LIST_SEARCH_KEYS.products) ?? {}

    if (isLoading) {
        return (
            <div className="py-8 text-center text-sm text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }
    if (!product) {
        return (
            <div className="py-8 text-center text-sm text-destructive">
                {m.product_not_found()}
            </div>
        )
    }

    const handleUpdate = async (data: UpdateProductPayload) => {
        try {
            await updateProduct.mutateAsync({ id: productId, data })
            toast.success(m.product_update_success())
        } catch {
            toast.error(m.product_update_error())
        }
    }

    const handleDelete = async () => {
        if (!confirm(m.product_delete_confirm())) return
        try {
            await deleteProduct.mutateAsync(productId)
            toast.success(m.toast_product_delete_success())
            void navigate({ to: '/products', search: backSearch() })
        } catch {
            toast.error(m.toast_product_delete_error())
        }
    }

    const handleImageUpload = async (file: File) => {
        try {
            await uploadImage.mutateAsync({ productId, file })
            toast.success(m.product_image_upload_success())
        } catch (err) {
            toast.error(
                m.product_image_upload_error({
                    msg: err instanceof Error ? err.message : 'Unknown error',
                }),
            )
        }
    }

    const handleDeleteImage = async (imageId: string) => {
        try {
            await deleteImage.mutateAsync({ productId, imageId })
            toast.success(m.toast_product_image_delete_success())
        } catch {
            toast.error(m.toast_product_image_delete_error())
        }
    }

    const handleSetPrimary = async (imageId: string) => {
        try {
            await setPrimary.mutateAsync({ productId, imageId })
            toast.success(m.toast_product_image_primary_success())
        } catch {
            toast.error(m.product_update_error())
        }
    }

    return (
        <div className="space-y-4 p-4 md:space-y-6 md:p-6">
            {/* Header — trạng thái đọc được ngay cạnh tên, đồng nhất với màn đơn hàng. */}
            <div className="flex flex-wrap items-start gap-3">
                <Button asChild variant="ghost" size="icon">
                    <Link to="/products" search={backSearch()}>
                        <ArrowLeft className="size-4" />
                    </Link>
                </Button>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-semibold">
                            {product.name}
                        </h1>
                        <Badge
                            variant={product.is_active ? 'success' : 'outline'}
                        >
                            {product.is_active
                                ? m.product_active()
                                : m.product_inactive()}
                        </Badge>
                        {product.is_sellable && (
                            <Badge variant="outline">
                                {m.product_sellable_badge()}
                            </Badge>
                        )}
                        {product.product_type === 'combo' && (
                            <Badge variant="secondary">
                                {m.product_combo_badge()}
                            </Badge>
                        )}
                    </div>
                    <p className="mt-0.5 font-mono text-sm text-muted-foreground">
                        {product.sku}
                    </p>
                </div>
                <Button variant="destructive" size="sm" onClick={handleDelete}>
                    <Trash2 className="size-4" />
                    {m.common_delete()}
                </Button>
            </div>

            {/* grid-cols-1 chứ không để mặc định: track ngầm co theo max-content
                nên bảng tồn rộng sẽ đẩy cả trang trượt ngang trên mobile. */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(320px,380px)_minmax(0,1fr)] lg:items-start">
                {/* Cột hẹp — ảnh sản phẩm vốn không cần bề ngang. */}
                <div className="min-w-0 space-y-5 lg:col-start-1 lg:row-start-1">
                    <FormSection
                        icon={<Images />}
                        title={m.product_images_section_title()}
                    >
                        <ProductImageGallery
                            productName={product.name}
                            images={product.images ?? []}
                            isUploading={uploadImage.isPending}
                            onUpload={handleImageUpload}
                            onDelete={handleDeleteImage}
                            onSetPrimary={handleSetPrimary}
                        />
                    </FormSection>
                </div>

                {/* Cột rộng — form và các bảng cần chỗ. */}
                <div className="min-w-0 space-y-5 lg:col-start-2 lg:row-start-1">
                    <FormSection
                        icon={<Package />}
                        title={m.product_info_section_title()}
                    >
                        <ProductForm
                            product={product}
                            onSubmit={handleUpdate}
                            isLoading={updateProduct.isPending}
                        />
                    </FormSection>

                    <ProductStockPanel data={stock} isLoading={stockLoading} />

                    <ComboRecipeSection
                        productId={productId}
                        productType={product.product_type}
                    />
                </div>
            </div>
        </div>
    )
}
